import { KVRow, KVRowType } from 'new/elements/KeyValueEditor';
import { useEffect, useRef, useState } from 'react';
import { setKwargsErrors } from './kwargsValidationState';

let rowIdCounter = 0;
const nextRowId = () => `kv-row-${++rowIdCounter}`;

// ── Pure serialization helpers ────────────────────────────────────────────────

export function parseExtraData(raw?: string): Record<string, unknown> {
  if (!raw) return {};
  try {
    return (JSON.parse(raw) as Record<string, unknown>) ?? {};
  } catch {
    return {};
  }
}

function makeEmptyRow(): KVRow {
  return { id: nextRowId(), key: '', value: '', type: 'string' };
}

function inferType(value: unknown): KVRowType {
  if (typeof value === 'number') return 'number';
  return 'string';
}

function castValue(value: string, type: KVRowType): unknown {
  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

export function parseKwargsToRows(extraData?: string): KVRow[] {
  const parsed = parseExtraData(extraData);
  const kwargs = (parsed?.custom_code as any)?.kwargs;
  if (!kwargs || typeof kwargs !== 'object' || Object.keys(kwargs).length === 0) {
    return [makeEmptyRow()];
  }
  return Object.entries(kwargs as Record<string, unknown>).map(([key, value]) => ({
    id: nextRowId(),
    key,
    value:
      value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
    type: inferType(value),
  }));
}

export function serializeKwargsToExtraData(
  existingExtraData: string | undefined,
  rows: KVRow[],
): string {
  const parsed = parseExtraData(existingExtraData);
  const kwargs = Object.fromEntries(
    rows.filter((r) => r.key.trim()).map((r) => [r.key, castValue(r.value, r.type)]),
  );
  return JSON.stringify({
    ...parsed,
    custom_code: {
      ...((parsed.custom_code as object) ?? {}),
      kwargs,
    },
  });
}

export function clearKwargsFromExtraData(config: any): void {
  if (!config.extra_data) return;
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(config.extra_data) as Record<string, unknown>;
  } catch {
    return; // leave extra_data untouched on parse error
  }
  if (parsed.custom_code && typeof parsed.custom_code === 'object') {
    delete (parsed.custom_code as any).kwargs;
    if (Object.keys(parsed.custom_code as object).length === 0) {
      delete parsed.custom_code;
    }
  }
  const remaining = Object.keys(parsed);
  config.extra_data = remaining.length > 0 ? JSON.stringify(parsed) : undefined;
}

export function getDuplicateRowIds(rows: KVRow[]): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const row of rows) {
    const k = row.key.trim();
    if (!k) continue;
    if (seen.has(k)) {
      dupes.add(row.id);
    } else {
      seen.add(k);
    }
  }
  return dupes;
}

export function getMissingKeyRowIds(rows: KVRow[]): Set<string> {
  const missing = new Set<string>();
  for (const row of rows) {
    if (!row.key.trim() && row.value.trim()) {
      missing.add(row.id);
    }
  }
  return missing;
}

export function getInvalidNumberRowIds(rows: KVRow[]): Set<string> {
  const invalid = new Set<string>();
  for (const row of rows) {
    if (row.type !== 'number') continue;
    const trimmed = row.value.trim();
    if (!trimmed) continue;
    if (!Number.isFinite(Number(trimmed))) {
      invalid.add(row.id);
    }
  }
  return invalid;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseAIFunctionKwargsParams {
  sourceConfig: any;
  onSourceConfigChanged: (config: any) => void;
}

interface UseAIFunctionKwargsReturn {
  rows: KVRow[];
  duplicateRowIds: Set<string>;
  missingKeyRowIds: Set<string>;
  invalidNumberRowIds: Set<string>;
  /** Called by the editor's onChange — updates rows and propagates extra_data. */
  handleKwargsChange: (newRows: KVRow[]) => void;
}

export function useAIFunctionKwargs({
  sourceConfig,
  onSourceConfigChanged,
}: UseAIFunctionKwargsParams): UseAIFunctionKwargsReturn {
  const [rows, setRows] = useState<KVRow[]>(() => parseKwargsToRows(sourceConfig?.extra_data));
  const [duplicateRowIds, setDuplicateRowIds] = useState<Set<string>>(new Set());
  const [missingKeyRowIds, setMissingKeyRowIds] = useState<Set<string>>(new Set());
  const [invalidNumberRowIds, setInvalidNumberRowIds] = useState<Set<string>>(new Set());

  // Track chunker identity across renders so we can reset row state reactively
  // when the user switches chunkers. Doing the reset inside the dropdown's
  // synchronous onChange handler interleaves setState calls with the parent's
  // onSourceConfigChanged propagation loop and causes the first click to be
  // "swallowed" (visible state stays on the old chunker). Reacting via effect
  // sidesteps that ordering hazard.
  const currentChunkerId: string | null = sourceConfig?.['ai.function.id'] ?? null;
  const prevChunkerIdRef = useRef<string | null>(currentChunkerId);

  useEffect(() => {
    if (currentChunkerId === prevChunkerIdRef.current) return;
    prevChunkerIdRef.current = currentChunkerId;
    setRows([makeEmptyRow()]);
    setDuplicateRowIds(new Set());
    setMissingKeyRowIds(new Set());
    setInvalidNumberRowIds(new Set());
    setKwargsErrors(false);

    // Strip stale kwargs left over from the previous chunker. Doing this
    // here (after the chunker switch has already propagated) instead of
    // synchronously in the dropdown's onChange handler avoids a race where
    // mutating extra_data in the same update as ai.function.id caused
    // react-select to revert to the previous selection on first click.
    if (sourceConfig?.extra_data) {
      const cleaned = { ...sourceConfig };
      clearKwargsFromExtraData(cleaned);
      if (cleaned.extra_data !== sourceConfig.extra_data) {
        onSourceConfigChanged(cleaned);
      }
    }
  }, [currentChunkerId]);

  useEffect(() => {
    return () => setKwargsErrors(false);
  }, []);

  const handleKwargsChange = (newRows: KVRow[]) => {
    const dupes = getDuplicateRowIds(newRows);
    const missing = getMissingKeyRowIds(newRows);
    const invalidNumbers = getInvalidNumberRowIds(newRows);
    setRows(newRows);
    setDuplicateRowIds(dupes);
    setMissingKeyRowIds(missing);
    setInvalidNumberRowIds(invalidNumbers);

    setKwargsErrors(dupes.size > 0 || missing.size > 0 || invalidNumbers.size > 0);

    const updatedConfig = { ...sourceConfig };
    const hasValidRows = newRows.some((r) => r.key.trim());
    if (hasValidRows) {
      updatedConfig.extra_data = serializeKwargsToExtraData(sourceConfig?.extra_data, newRows);
    } else {
      clearKwargsFromExtraData(updatedConfig);
    }
    onSourceConfigChanged(updatedConfig);
  };

  return {
    rows,
    duplicateRowIds,
    missingKeyRowIds,
    invalidNumberRowIds,
    handleKwargsChange,
  };
}
